import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, User, FileText, Calendar } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    from_name: '',
    reply_to: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Update the form's message field to include contact details
    if (formRef.current) {
      const messageInput = formRef.current.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
      const contactDetails = `Name: ${formData.from_name}\nEmail: ${formData.reply_to}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`;
      messageInput.value = contactDetails;
    }

    try {
      const result = await emailjs.sendForm(
        'service_l7n36yv',
        'template_pcoocgf',
        formRef.current!,
        '3pGgUTaif-_dIInLH'
      );

      if (result.text === 'OK') {
        toast.success('Message sent successfully!');
        setFormData({
          from_name: '',
          reply_to: '',
          phone: '',
          subject: '',
          message: '',
        });
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <ToastContainer position="bottom-right" />
      
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-24">
        <div className="container-width">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">Get in Touch</h1>
            <p className="text-xl text-slate-300">
              Have a question or ready to start your project? We're here to help turn your ideas into reality.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-slate-50">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-24 items-start">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Send us a message</h2>
                  <p className="text-slate-600">we promise not to leave you on read (probably).</p>
                </div>

                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="from_name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="input-icon" />
                      <input
                        type="text"
                        id="from_name"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        placeholder="So we know what to call you"
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reply_to" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="input-icon" />
                      <input
                        type="email"
                        id="reply_to"
                        name="reply_to"
                        value={formData.reply_to}
                        onChange={handleChange}
                        placeholder="So we can actually reply"
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="input-icon" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="So we can reach you quickly"
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                      Subject
                    </label>
                    <div className="relative">
                      <FileText className="input-icon" />
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="To pretend we're organized"
                        className="input-with-icon"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message
                    </label>
                    <div className="relative">
                      <MessageSquare className="textarea-icon" />
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        placeholder="Tell us what you need (or just say hi)"
                        className="textarea-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
                             transition transform hover:scale-[1.02] flex items-center justify-center group
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className={`w-5 h-5 mr-2 ${isSubmitting ? 'animate-pulse' : 'group-hover:translate-x-1 transition-transform duration-300'}`} />
                    {isSubmitting ? 'Sending...' : "Hit the button. Let's see where this goes.."}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-slate-900 rounded-2xl p-8 sm:p-10 text-white">
                <h2 className="text-2xl font-bold mb-8">Contact Information</h2>
                <div className="space-y-8">
                  <ContactInfo
                    icon={<MapPin className="w-6 h-6" />}
                    title="Our Location"
                    content="Katraj, Pune"
                  />
                  <ContactInfo
                    icon={<Phone className="w-6 h-6" />}
                    title="Phone Number"
                    content={
                      <a 
                        href="tel:+917522979568" 
                        className="hover:text-blue-300 transition"
                      >
                        +91 7522979568
                      </a>
                    }
                  />
                  <ContactInfo
                    icon={<Mail className="w-6 h-6" />}
                    title="Email Address"
                    content={
                      <a 
                        href="mailto:support@visrupt.com" 
                        className="hover:text-blue-300 transition"
                      >
                        support@visrupt.in
                      </a>
                    }
                  />
                </div>

                <div className="mt-12 pt-12 border-t border-white/10">
                  <h3 className="text-xl font-semibold mb-6">Schedule a Meeting</h3>
                  <a
                    href="https://calendly.com/visrupt/schedule-a-call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl text-white font-semibold transition-all duration-300 group w-full justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">Book a Meeting</span>
                  </a>
                </div>

                <div className="mt-12 pt-12 border-t border-white/10">
                  <h3 className="text-xl font-semibold mb-4">Connect With Us</h3>
                  <p className="text-slate-300">Follow us on LinkedIn... because, of course, you definitely don’t want to miss any of our updates. Or, you know, just say 'Hi' on WhatsApp if you prefer keeping it casual.
                  </p>
                  <div className="mt-6 flex space-x-4">
                    <a 
                      href="https://www.linkedin.com/company/visrupt" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-white/10 p-3 rounded-lg hover:bg-white/20 transition"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://wa.me/917522979568" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-white/10 p-3 rounded-lg hover:bg-white/20 transition"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const ContactInfo = ({ icon, title, content }) => (
  <div className="flex items-start space-x-4">
    <div className="bg-white/10 p-3 rounded-lg">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-lg mb-1">{title}</h3>
      <div className="text-slate-300">{content}</div>
    </div>
  </div>
);

export default Contact;